// libs
import { Dispatch } from "react";

// defs
import { IAction } from "@defs/Action";
import { ISimulationInput } from "@defs/Utilities";
import { InputType } from "@utils/appsync/schema/API";
import { Scenario, Sim } from "@utils/appsync/schema/API";
import { IStepper } from "@components/Stepper/Stepper.props";
import { INewSimulationState } from "@store/state/NewSimulation";

// constants
import { OpexAllocationSteppersType } from "@pages/simulations/NewSimulation/containers/OpexAllocation/constants";

export const enum NewSimulationActionType {
    INITIALIZE_NEW_SIMULATION_STATE = "INITIALIZE_NEW_SIMULATION_STATE",
    UPDATE_NEW_SIMULATION_STATE = "UPDATE_NEW_SIMULATION_STATE",
    SET_READ_ONLY = "SET_READ_ONLY",
    SET_SIMULATION_ID = "SET_SIMULATION_ID",
    SET_SIMULATION_DATA = "SET_SIMULATION_DATA",
    SET_SELECTED_SCENARIO = "SET_SELECTED_SCENARIO",
    SET_SCENARIO_LIST = "SET_SCENARIO_LIST",
    SET_SELECTED_COST_POOL_LIST = "SET_SELECTED_COST_POOL_LIST",
    SET_SIMULATION_STEPPERS = "SET_SIMULATION_STEPPERS",
    SET_ACTIVE_SIMULATION_STEPPER = "SET_ACTIVE_SIMULATION_STEPPER",
    SET_SIMULATION_INPUTS = "SET_SIMULATION_INPUTS",
    SET_IS_SYSTEM_DEFINITION_AND_MAPPING_SELECTED = "SET_IS_SYSTEM_DEFINITION_AND_MAPPING_SELECTED",
}

// Action to initialize the new simulation state.
export const initializeNewSimulationState = (dispatch: Dispatch<INewSimulationAction>) => {
    return (newSimulationState: INewSimulationState) => {
        dispatch({
            type: NewSimulationActionType.INITIALIZE_NEW_SIMULATION_STATE,
            payload: newSimulationState,
        });
    };
};

// Action to update the new simulation state.
export const updateNewSimulationState = (dispatch: Dispatch<INewSimulationAction>) => {
    return (updatedNewSimulationState: INewSimulationState) => {
        dispatch({
            type: NewSimulationActionType.UPDATE_NEW_SIMULATION_STATE,
            payload: updatedNewSimulationState,
        });
    };
};

// Action to set simulation id
export const setReadOnly = (dispatch: Dispatch<INewSimulationAction>) => {
    return (readOnly: boolean) => {
        dispatch({
            type: NewSimulationActionType.SET_READ_ONLY,
            payload: {
                readOnly,
            },
        });
    };
};

// Action to set simulation id
export const setSimulationId = (dispatch: Dispatch<INewSimulationAction>) => {
    return (simulationId: string) => {
        dispatch({
            type: NewSimulationActionType.SET_SIMULATION_ID,
            payload: {
                simulationId,
            },
        });
    };
};

// Action to set simulation data
export const setSimulationData = (dispatch: Dispatch<INewSimulationAction>) => {
    return (simulationData: Sim) => {
        dispatch({
            type: NewSimulationActionType.SET_SIMULATION_DATA,
            payload: {
                simulationData,
            },
        });
    };
};
// Action to set selected scenario
export const setSelectedScenario = (dispatch: Dispatch<INewSimulationAction>) => {
    return (selectedScenario: Scenario) => {
        dispatch({
            type: NewSimulationActionType.SET_SELECTED_SCENARIO,
            payload: {
                selectedScenario,
            },
        });
    };
};

// Action to set scenario list
export const setScenarioList = (dispatch: Dispatch<INewSimulationAction>) => {
    return (scenarioList: Array<Scenario>) => {
        dispatch({
            type: NewSimulationActionType.SET_SCENARIO_LIST,
            payload: {
                scenarioList,
            },
        });
    };
};

// Action to set cost pool list
export const setSelectedCostPoolList = (dispatch: Dispatch<INewSimulationAction>) => {
    return (selectedCostPoolList: Array<string>) => {
        dispatch({
            type: NewSimulationActionType.SET_SELECTED_COST_POOL_LIST,
            payload: {
                selectedCostPoolList: selectedCostPoolList,
            },
        });
    };
};

// Action to set simulation steppers
export const setSimulationSteppers = (dispatch: Dispatch<INewSimulationAction>) => {
    return (simulationSteppers: Array<IStepper<OpexAllocationSteppersType>>) => {
        dispatch({
            type: NewSimulationActionType.SET_SIMULATION_STEPPERS,
            payload: {
                simulationSteppers,
            },
        });
    };
};

// Action to set active simulation stepper
export const setActiveSimulationStepper = (dispatch: Dispatch<INewSimulationAction>) => {
    return (activeSimulationStepper: IStepper<OpexAllocationSteppersType>) => {
        dispatch({
            type: NewSimulationActionType.SET_ACTIVE_SIMULATION_STEPPER,
            payload: {
                activeSimulationStepper,
            },
        });
    };
};

// Action to set input types
export const setSimulationInputs = (dispatch: Dispatch<INewSimulationAction>) => {
    return (simulationInputs: Array<ISimulationInput<InputType>>) => {
        dispatch({
            type: NewSimulationActionType.SET_SIMULATION_INPUTS,
            payload: { simulationInputs: simulationInputs },
        });
    };
};

// Action to set isSystemDefinitionAndMappingSelected
export const setIsSystemDefinitionAndMappingSelected = (dispatch: Dispatch<INewSimulationAction>) => {
    return (isSystemDefinitionAndMappingSelected: boolean) => {
        dispatch({
            type: NewSimulationActionType.SET_IS_SYSTEM_DEFINITION_AND_MAPPING_SELECTED,
            payload: { isSystemDefinitionAndMappingSelected },
        });
    };
};

export interface INewSimulationAction extends IAction<NewSimulationActionType, INewSimulationState> {}
